import { useNavigate, useParams } from 'react-router-dom';
import React, { useMemo, useState } from 'react';
import { CreateGroupDto, CreateLocationsWithGroups, GroupDto } from '../../common/interfaces/kindergarten.interface';
import { createLocations } from '../../api/kindergarten.api';
import { Autocomplete, Box, Breadcrumbs, IconButton, Link, Paper, Stack, Typography, useTheme } from '@mui/material';
import { getTimezones } from '../../utilities/helper/timezone.helper';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupModal from '../../components/GroupModal';
import { ActionMode, KindergartenDetailsTabs } from '../../common/enums/common.enum';
import { ReactComponent as EditIcon } from '../../assets/edit-icon.svg';
import { createNotification } from '../../utilities/helper/notification.helper';
import { MESSAGE_TYPE } from '../../common/constants/common.constants';
import { locationGroupBadgeStyle, locationPaperStyle } from './CreateLocation.styles';
import GenericButton, { GenericButtonTypeEnum } from '../../components/GenericButton';
import GenericTextField from '../../components/GenericTextField/GenericTextField';
import { useTranslation } from 'react-i18next';

const CreateLocation = () => {
  const { t } = useTranslation();
  const { kindergartenId, kindergartenName } = useParams<{
    kindergartenId: string;
    kindergartenName: string;
  }>();

  const navigate = useNavigate();

  const [locations, setLocations] = useState<CreateLocationsWithGroups[]>([
    { name: '', address: '', timezone: 'Europe/Belgrade', groups: [] }
  ]);

  const [selectedLocationIndex, setSelectedLocationIndex] = useState<number | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<{
    locationIndex?: number;
    groupIndex?: number;
    group: GroupDto;
  } | null>(null);

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [locationErrors, setLocationErrors] = useState<{ name?: boolean; address?: boolean; timezone?: boolean }[]>(
    locations.map(() => ({ name: false, address: false, timezone: false }))
  );

  const theme = useTheme();
  const timezones = useMemo(() => getTimezones(), []);

  const handleAddLocation = () => {
    setLocations(prev => [...prev, { name: '', address: '', timezone: 'Europe/Belgrade', groups: [] }]);
    setLocationErrors(prev => [...prev, { name: false, address: false, timezone: false }]);
  };

  const handleRemoveLocation = (index: number) => {
    setLocations(prev => prev.filter((_, i) => i !== index));
    setLocationErrors(prev => prev.filter((_, i) => i !== index));
  };
  const handleSubmit = async () => {
    try {
      const errors = locations.map(loc => ({
        name: !loc.name.trim(),
        address: !loc.address.trim(),
        timezone: !loc.timezone.trim()
      }));

      setLocationErrors(errors);

      const hasErrors = errors.some(err => err.name || err.address || err.timezone);

      if (hasErrors) {
        return;
      }
      if (kindergartenId) {
        await createLocations(kindergartenId, locations);
        createNotification(MESSAGE_TYPE.SUCCESS, t('locations.created'));
        navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.LocationsAndGroups}`);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('locations.createFailed'));
    }
  };
  const handleAddGroup = (group: CreateGroupDto, keepOpen: boolean = false) => {
    if (selectedLocationIndex === null) return;

    setLocations(prev => prev.map((loc, i) => (i === selectedLocationIndex ? { ...loc, groups: [...(loc.groups ?? []), group] } : loc)));
    if (!keepOpen) {
      setGroupModalOpen(false);
    }
  };

  const handleUpdateGroup = (updatedGroup: CreateGroupDto) => {
    if (selectedGroup) {
      const { locationIndex, groupIndex } = selectedGroup;

      setLocations(prev =>
        prev.map((loc, i) =>
          i === locationIndex
            ? {
                ...loc,
                groups: loc.groups?.map((group, idx) => (idx === groupIndex ? { ...group, ...updatedGroup } : group))
              }
            : loc
        )
      );
      setGroupModalOpen(false);
    }
  };

  const handleDeleteGroup = () => {
    if (selectedGroup) {
      setLocations(prev =>
        prev.map((loc, i) =>
          i === selectedGroup.locationIndex
            ? {
                ...loc,
                groups: loc.groups?.filter((_, idx) => idx !== selectedGroup.groupIndex) || []
              }
            : loc
        )
      );
      setGroupModalOpen(false);
    }
  };

  const openGroupModal = (index: number) => {
    setSelectedGroup(null);
    setSelectedLocationIndex(index);
    setGroupModalOpen(true);
  };

  const openEditGroupModal = (locationIndex: number, groupIndex: number, group: CreateGroupDto) => {
    setSelectedGroup({ locationIndex, groupIndex, group });
    setGroupModalOpen(true);
  };

  return (
    <Box p={7}>
      <Breadcrumbs aria-label={t('common.breadcrumb')} sx={{ mb: 3 }}>
        <Link color="text.secondary" underline="hover" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          {t('nav.kindergartens')}
        </Link>
        {kindergartenName && (
          <Link
            color="text.secondary"
            underline="hover"
            onClick={() => navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.LocationsAndGroups}`)}
            sx={{ cursor: 'pointer' }}
          >
            {kindergartenName}
          </Link>
        )}
        <Typography color="text.primary">{t('locations.create')}</Typography>
      </Breadcrumbs>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h1">{t('locations.create')}</Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <GenericButton
            buttonType={GenericButtonTypeEnum.Secondary}
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddLocation}
            label={t('common.createAnother')}
          />
          <GenericButton buttonType={GenericButtonTypeEnum.Primary} variant="contained" onClick={handleSubmit} label={t('common.save')} />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        {locations.map((location, locIndex) => (
          <Paper key={locIndex} sx={theme => locationPaperStyle(theme)}>
            {locations.length > 1 && (
              <IconButton
                onClick={() => handleRemoveLocation(locIndex)}
                size="small"
                aria-label={t('locations.remove')}
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  zIndex: 1
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
            <Stack spacing={2} sx={{ width: '100%' }}>
              <GenericTextField
                label={t('locations.name')}
                value={location.name}
                onChange={e => {
                  setLocations(prev => prev.map((loc, i) => (i === locIndex ? { ...loc, name: e.target.value } : loc)));
                  setLocationErrors(prev => prev.map((err, i) => (i === locIndex ? { ...err, name: false } : err)));
                }}
                required
                error={locationErrors[locIndex]?.name}
              />
              <GenericTextField
                label={t('common.address')}
                value={location.address}
                onChange={e => {
                  const newValue = e.target.value;
                  setLocations(prev => prev.map((loc, i) => (i === locIndex ? { ...loc, address: newValue } : loc)));
                  setLocationErrors(prev => prev.map((err, i) => (i === locIndex ? { ...err, address: false } : err)));
                }}
                required
                error={locationErrors[locIndex]?.address}
              />

              <Autocomplete
                fullWidth
                options={timezones}
                getOptionLabel={option => option.label}
                value={timezones.find(tz => tz.value === location.timezone) || null}
                onChange={(_, newValue) => {
                  setLocations(prev => prev.map((loc, i) => (i === locIndex ? { ...loc, timezone: newValue?.value || '' } : loc)));
                  setLocationErrors(prev => prev.map((err, i) => (i === locIndex ? { ...err, timezone: false } : err)));
                }}
                renderInput={params => (
                  <GenericTextField
                    {...params}
                    label={t('locations.timezone')}
                    placeholder={t('locations.searchTimezone')}
                    sx={{ marginBottom: 0 }}
                    required
                    error={locationErrors[locIndex]?.timezone}
                  />
                )}
                sx={{ width: '100%', marginBottom: '12px' }}
              />
              <GenericButton
                buttonType={GenericButtonTypeEnum.Neutral}
                variant="text"
                startIcon={<AddIcon />}
                onClick={() => openGroupModal(locIndex)}
                label={t('groups.create')}
                sx={{
                  alignSelf: 'flex-start',
                  justifyContent: 'flex-start',
                  textAlign: 'left'
                }}
              />
              {location.groups && location.groups.length > 0 && (
                <Box mt={2} sx={{ width: '100%' }}>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {location.groups.map((group, groupIndex) => (
                      <Box key={groupIndex} display="inline-flex" alignItems="center" gap="4px" sx={locationGroupBadgeStyle}>
                        <span>{group.name}</span>
                        <EditIcon
                          fontSize="small"
                          style={{ cursor: 'pointer' }}
                          onClick={() => openEditGroupModal(locIndex, groupIndex, group)}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          </Paper>
        ))}
      </Box>

      {selectedLocationIndex !== null && (
        <GroupModal
          open={groupModalOpen}
          onClose={() => setGroupModalOpen(false)}
          kindergartenId={kindergartenId!}
          mode={selectedGroup ? ActionMode.EDIT : ActionMode.CREATE}
          group={selectedGroup?.group}
          bulkMode={true}
          onSubmit={selectedGroup ? handleUpdateGroup : handleAddGroup}
          onDelete={selectedGroup ? handleDeleteGroup : undefined}
        />
      )}
    </Box>
  );
};

export default CreateLocation;
